import React, { useState, useEffect, useCallback, useMemo } from "react"
import { Empty, Flex, Card, Col, Row } from "antd"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import BookingEmptyImage from "ui/Icon/DSIcon/BookingEmptyImage"
import { getGuideBookingsV2 } from "store/bookings"
import { getStatusMessages, PAYMENT_STATUS } from "./constants"
import { displayPaymentStatus } from "ui/Booking/Details/utils"
import StatusTag from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/StatusTag"
import BookingsHeader from "./Header"
import StatusTabs from "./StatusBar"
import BookingTable from "./BookingTable"
import WaiverStatus from "./BookingTable/WaiverStatus"
import styles from "./Index.module.css"
import { debounce } from "lodash"
import { BookingListing } from "./BookingListing"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import moment from "moment"
import LoadMoreButton from "components/common/LoadMore"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { Text } from "ui/Typography"
import { ExclamationCircleOutlined } from "@ant-design/icons"
import { routes } from "router"

const DELAY_TIME = 300
const getDefaultParams = () => {
  const today = new Date().toISOString().split("T")[0] // "YYYY-MM-DD"
  return {
    range: "month",
    date: today,
    type: "booked",
    sort: "asc",
    search: ""
  }
}

const Bookings = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const statusMessages = getStatusMessages(t)
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sortKey, setSortKey] = useState("booked_date")
  const [sortOrder, setSortOrder] = useState("asc")
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const range = searchParams?.get("range")
  const type = searchParams?.get("type")
  const date = searchParams?.get("date")
  const sort = searchParams?.get("sort")
  const search = searchParams?.get("search")
  const navigate = useNavigate()

  useLoaderWatchers({ watchers: ["bookings"] })
  const { items, loading, metadata } = useSelector((store) => store.bookings)

  const onSortChange = (pagination, filter, sorter) => {
    setSortKey(sorter?.field ? sorter?.field : "booked_date")
    setSortOrder(sorter?.order === "ascend" ? "asc" : "desc")
    setPage(1) // Reset to first page when sorting changes
  }

  const debouncedUpdate = useMemo(
    () =>
      debounce((value) => {
        const params = new URLSearchParams(value.toString())
        params.set("search", value)
      }, DELAY_TIME),
    [setSearchParams]
  )

  const onChangeFilters = useCallback(
    ({ target }) => {
      setPage(1)
      if (target.type == "search") {
        debouncedUpdate(target.value)
      } else {
        const updatedParams = new URLSearchParams(searchParams.toString())
        updatedParams.set(target.name, target.value)
        setSearchParams(updatedParams)
      }
    },
    [debouncedUpdate]
  )

  useEffect(() => {
    return () => debouncedUpdate.cancel()
  }, [debouncedUpdate])

  const fetchBookings = useCallback(
    (nextPage = false, pageOverride = null) => {
      if (!date || !range || !type || !sort) return
      setSelectedRowKeys([])
      const data = {}
      const [year, month, day] = date.split("-")
      switch (range) {
        case "day":
          Object.assign(data, { day, month, year })
          break

        case "month":
          Object.assign(data, { month, year })
          break

        case "year":
          Object.assign(data, { year })
          break

        default:
          break
      }
      data["search"] = search
      data["sort_by"] = sortKey
      data["order"] = sortOrder
      data["type"] = type
      data["per_page"] = pageSize

      const requestedPage = pageOverride || page
      data["page"] = requestedPage

      dispatch(getGuideBookingsV2(data, requestedPage, nextPage))
    },
    [date, range, type, sort, search, sortOrder, sortKey, page, pageSize] //eslint-disable-line
  )

  const handleLoadMore = useCallback(() => {
    if (!metadata?.next_page) return
    fetchBookings(true, metadata.next_page)
  }, [fetchBookings, metadata?.next_page])

  const listData = useMemo(() => {
    return items.map((row) => {
      const paymentStatus = displayPaymentStatus(row?.payment_status)
      const showExclamation = row.payment_status === PAYMENT_STATUS.partial && row?.is_full_payment

      const isMultiDay = row.duration_type === "multi_day"

      return {
        "Experience Date & Time": isMultiDay
          ? `${row.experience_date || ""}, ${row.experience_time?.start_time || ""}`
          : `${row.experience_date || ""}, ${row.experience_time?.start_time || ""} - ${row.experience_time?.end_time || ""}`,
        "Client Name": row?.full_name || "",
        Experience: row?.trip_title || "",
        "Total Amount": row?.total_amount || 0,
        "Payment Status": (
          <Flex align="center" gap={4} flex={1}>
            <StatusTag status={paymentStatus} bookingStatus={type} />
            {showExclamation && <ExclamationCircleOutlined style={{ color: "#F98600" }} />}
          </Flex>
        ),
        ...(row?.is_waiver_attached && row?.total_guest_count
          ? {
              Waiver: (
                <Flex align="center" flex={1} className="ml-xs">
                  <WaiverStatus
                    isWaiverAttached={row?.is_waiver_attached}
                    totalGuestCount={row?.total_guest_count}
                    signedWaiverCount={row?.signed_waiver_count}
                  />
                </Flex>
              )
            }
          : {}),
        "Booked On": row?.create_at ? moment(row?.create_at).format("MM/DD/YYYY, hh:mm A") : "",
        "Booking ID": row?.booking_id,
        id: row?.uuid
      }
    })
  }, [items, type])

  useEffect(() => {
    const hasAnyParam = Array.from(searchParams.keys()).length > 0
    if (!hasAnyParam) {
      setSearchParams(getDefaultParams())
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    fetchBookings(false)
  }, [fetchBookings])

  const handleBookingListItemClick = (booking) => {
    navigate(routes.guideBookingDetailsPath({ id: booking?.id }))
  }

  return (
    <FirstLoading name="bookings">
      <>
        <BookingsHeader onChangeFilters={onChangeFilters} />
        <StatusTabs onChangeFilters={onChangeFilters} />
        <div className={styles.bookingsContainer} id="bookings">
          <WithEmptyStateWrapper
            name="Booking"
            hasItems={items.length > 0}
            fallbackTitle={"abc"}
            fallback={
              loading ? (
                <></>
              ) : (
                <div className={styles.contentWrapper}>
                  <Empty
                    image={<BookingEmptyImage />}
                    description={
                      <>
                        <Text strong size="lg">
                          {statusMessages[type]?.title}
                        </Text>
                        <p style={{ color: "#888" }}>{statusMessages[type]?.description}</p>
                      </>
                    }
                  />
                </div>
              )
            }
          >
            <Card className="mb-m" styles={{ body: { padding: "0px !important" } }}>
              <Row gutter={[0, 16]}>
                <Col xs={24} md={0}>
                  <Flex vertical gap={15}>
                    <BookingListing data={listData} onItemClick={handleBookingListItemClick} />
                    <LoadMoreButton
                      currentPage={metadata?.page || 1}
                      totalPages={Math.ceil(metadata.total_results / pageSize)}
                      loading={loading}
                      onLoadMore={handleLoadMore}
                      totalAvailable={metadata.total_results}
                    />
                  </Flex>
                </Col>
                <Col xs={0} md={24}>
                  <BookingTable
                    page={page}
                    pageSize={pageSize}
                    sortKey={sortKey}
                    sortOrder={sortOrder}
                    fetchBookings={fetchBookings}
                    onPageChange={setPage}
                    onPageSizeChange={(newSize) => {
                      setPageSize(newSize)
                      setPage(1)
                    }}
                    onSortChange={onSortChange}
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                  />
                </Col>
              </Row>
            </Card>
          </WithEmptyStateWrapper>
        </div>
      </>
    </FirstLoading>
  )
}

export default Bookings
